/**
 * BoFH API - Booking creation payload
 */
export interface BookingCreation {
  /**
   * Brand code to override the domain brand mapping, must only be used for test purposes, i.e
   * tests on e2e environment
   */
  brandCode?: string
  /**
   * Represents the person who performs the booking
   */
  customer: Customer
  customerAccount: CustomerAccount
  /**
   * Offer metadata
   */
  offerMetadata: OfferMetadata
  /**
   * Flag to enable falling back to similar offers when the original offer isn't available at
   * booking time
   */
  parallelOfferCheckEnabled?: boolean
  /**
   * Payment
   */
  payment: Payment
  /**
   * Rooms
   */
  rooms: Room[]
  /**
   * A metadata JSON object with params to be sent to the 3DS provider. The structure vary by
   * 3DS provider.
   */
  threeDomainSecureParams?: {[key: string]: any}
}

/**
 * Represents the person who performs the booking
 */
export interface Customer {
  email: string
  firstName: string
  lastName: string
  phone: Phone
}

/**
 * Phone
 */
export interface Phone {
  /**
   * Phone country code
   */
  countryCode: string
  /**
   * Phone number
   */
  number: string
}

/**
 * Guest Customer Account
 *
 * Registered Customer Account
 */
export interface CustomerAccount {
  /**
   * Type of the account
   */
  type: CustomerAccountType
  /**
   * Authentication method used by the account
   */
  authenticationMethod?: string
  /**
   * The date and time when the account was created
   */
  createdAt?: string
  email?: string
  /**
   * Indicates if the customer account email was verified
   */
  emailVerified?: boolean
  firstName?: string
  /**
   * ID of the customer account
   */
  id?: string
  /**
   * Last date and time account was logged in
   */
  lastLogin?: string
  lastName?: string
  /**
   * Indicates if the customer is a member plus
   */
  memberPlus?: boolean
  /**
   * Last date and time the account was updated
   */
  updatedAt?: string
}

/**
 * Type of the account
 */
export enum CustomerAccountType {
  Guest = 'guest',
  Registered = 'registered'
}

/**
 * Offer metadata
 */
export interface OfferMetadata {
  /**
   * Information to help keep membership experience across the whole booking funnel
   */
  accessTier: string[]
}

/**
 * Payment
 */
export interface Payment {
  billing: Billing
  fraudProtectionToken?: string
  /**
   * Wallet Payment Method Type
   *
   * Card Payment Method Type
   *
   * APM Payment Method Type
   */
  paymentMethodType: PaymentMethodType
  paymentSessionId?: string
  /**
   * Wallet payment
   */
  wallet?: Wallet
  /**
   * Credit card tokenized via PCI Proxy.
   *
   * Credit card tokenized via Stripe.
   */
  creditCard?: Card
}

/**
 * Billing details
 */
export interface Billing {
  /**
   * Billing contact address
   */
  address?: Address
  email: string
  firstName?: string
  lastName?: string
  phone: Phone
  /**
   * Tax identification number (11 digits for individual or 14 digits for company)
   */
  taxId?: string
}

/**
 * Billing contact address
 */
export interface Address {
  /**
   * Billing contact address city
   */
  city?: string
  /**
   * Billing contact address country code
   */
  countryCode: string
  line1?: string
  line2?: string
  /**
   * Billing contact address postal code
   */
  postalCode?: string
  /**
   * Billing contact address state code
   */
  stateCode?: string
}

/**
 * Credit card tokenized via PCI Proxy.
 *
 * Credit card tokenized via Stripe.
 */
export interface Card {
  /**
   * Credit card expiration month (2 characters)
   */
  expirationMonth?: string
  /**
   * Credit card expiration year (4 characters)
   */
  expirationYear?: string
  /**
   * Selected network to process the payment
   */
  network?: Network
  /**
   * PCI Proxy transaction ID
   */
  transactionId?: number
  /**
   * Credit card type
   */
  type?: CreditCardType
  /**
   * The ID associated to the payment method returned by Stripe.
   */
  id?: string
}

/**
 * Selected network to process the payment
 */
export enum Network {
  CartesBancaires = 'cartes_bancaires',
  MasterCard = 'master_card',
  Visa = 'visa'
}

/**
 * Credit card type
 */
export enum CreditCardType {
  Ax = 'AX',
  Bc = 'BC',
  CA = 'CA',
  Cu = 'CU',
  Dc = 'DC',
  Ds = 'DS',
  E = 'E',
  Jc = 'JC',
  R = 'R',
  T = 'T',
  To = 'TO',
  Vi = 'VI'
}

/**
 * Wallet Payment Method Type
 *
 * Card Payment Method Type
 *
 * APM Payment Method Type
 */
export enum PaymentMethodType {
  ApplePay = 'apple_pay',
  Card = 'card',
  GooglePay = 'google_pay',
  Ideal = 'ideal',
  Klarna = 'klarna',
  Paypal = 'paypal',
  RevolutPay = 'revolut_pay',
  Vipps = 'vipps'
}

/**
 * Wallet payment
 */
export interface Wallet {
  /**
   * The unique ID associated to the wallet to use in the transaction.
   */
  id: string
}

/**
 * Room
 */
export interface Room {
  /**
   * Room bed type ID
   */
  bedTypeId: string
  email: string
  firstName: string
  /**
   * The number of guests. Rooms are separated by pipe (|). Inside rooms there are adults and
   * children are separated by colon (:). Children ages are separated by comma (,).
   */
  guests: string
  lastName: string
  phone: Phone
  /**
   * Room smoking preference
   */
  smoking?: boolean
  /**
   * Room special request
   */
  specialRequest?: string
}
